import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SyncActionButtons = ({ 
  onStartSync, 
  canStartSync, 
  isLoading, 
  stats,
  className = '' 
}) => {
  const navigate = useNavigate();
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleStartSync = () => {
    if (stats.noMatches > 0) {
      setShowConfirmation(true);
    } else {
      onStartSync();
    }
  };

  const confirmStartSync = () => {
    setShowConfirmation(false);
    onStartSync();
  };

  const getSuccessRate = () => {
    const total = stats.totalTracks;
    const successful = stats.exactMatches + stats.similarMatches;
    return total > 0 ? Math.round((successful / total) * 100) : 0;
  };

  return (
    <>
      <div className={`bg-surface border border-border rounded-lg p-4 lg:p-6 ${className}`}>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          {/* Sync Summary */}
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-text-primary mb-2">
              Ready to Sync
            </h3>
            <div className="flex flex-wrap items-center gap-4 text-sm text-text-secondary">
              <div className="flex items-center space-x-1">
                <Icon name="Music" size={16} />
                <span>{stats.exactMatches + stats.similarMatches} tracks will be synced</span>
              </div>
              {stats.noMatches > 0 && (
                <div className="flex items-center space-x-1 text-warning">
                  <Icon name="AlertTriangle" size={16} />
                  <span>{stats.noMatches} tracks will be skipped</span>
                </div>
              )}
              <div className="flex items-center space-x-1 text-primary">
                <Icon name="TrendingUp" size={16} />
                <span>{getSuccessRate()}% success rate</span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              onClick={() => navigate('/playlist-selection-sync-setup')}
              iconName="ArrowLeft"
              iconSize={16}
              disabled={isLoading}
            >
              Back to Selection
            </Button>

            <Button
              variant="primary"
              size="lg"
              onClick={handleStartSync}
              disabled={!canStartSync || isLoading}
              loading={isLoading}
              iconName={isLoading ? undefined : "Play"}
              iconSize={20}
              className="min-w-32"
            >
              {isLoading ? 'Starting...' : 'Start Sync'}
            </Button>
          </div>
        </div>

        {/* Warning for low success rate */}
        {getSuccessRate() < 70 && (
          <div className="mt-4 p-3 bg-warning/10 border border-warning/20 rounded-lg">
            <div className="flex items-start space-x-2">
              <Icon name="AlertTriangle" size={16} className="text-warning mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-warning">
                  Low Success Rate Detected
                </p>
                <p className="text-xs text-text-muted mt-1">
                  Only {getSuccessRate()}% of tracks have good matches. Consider adjusting match sensitivity in Advanced Options or manually reviewing uncertain matches.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-1050 p-4">
          <div className="bg-surface border border-border rounded-lg max-w-md w-full p-6">
            <div className="flex items-start space-x-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
                <Icon name="AlertTriangle" size={20} className="text-warning" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-text-primary mb-2">
                  Confirm Sync with Missing Tracks
                </h3>
                <p className="text-sm text-text-secondary">
                  {stats.noMatches} track{stats.noMatches !== 1 ? 's' : ''} could not be matched and will be skipped during the sync process. 
                  {stats.exactMatches + stats.similarMatches > 0 && (
                    ` ${stats.exactMatches + stats.similarMatches} track${stats.exactMatches + stats.similarMatches !== 1 ? 's' : ''} will still be synced successfully.`
                  )}
                </p>
              </div>
            </div>

            <div className="bg-background rounded-lg p-3 mb-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-text-muted">Tracks to sync:</span>
                <span className="text-success font-medium">{stats.exactMatches + stats.similarMatches}</span>
              </div>
              <div className="flex items-center justify-between text-sm mt-1">
                <span className="text-text-muted">Tracks to skip:</span>
                <span className="text-warning font-medium">{stats.noMatches}</span>
              </div>
              <div className="flex items-center justify-between text-sm mt-2 pt-2 border-t border-border">
                <span className="text-text-primary font-medium">Success rate:</span>
                <span className="text-primary font-medium">{getSuccessRate()}%</span>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Button
                variant="ghost"
                onClick={() => setShowConfirmation(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={confirmStartSync}
                iconName="Play"
                iconSize={16}
                className="flex-1"
              >
                Continue Sync
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SyncActionButtons;